import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { createOfficeService, updateOfficeService, fetchOfficeServiceById } from "./supportContactService";
import "../assets/css/AddUser.css";
import "../assets/css/AddSupportContact.css";
import { getAuthItem } from "../utils/authStorage";
import { formatBusinessName, formatPascal } from "../utils/formatters";
import CountryCodeSelect from "../components/CountryCodeSelect";
import {
  DEFAULT_COUNTRY_CODE,
  formValueWithMobileSanitize,
  mobileValidationMessage,
  normalizeCountryCode
} from "../utils/phone";

/**
 * AddOfficeService
 *
 * Form to create a new office service.
 * Integrates with POST /create-office-service API.
 */
function AddOfficeService({ onBack, onAddService }) {
  const navigate = useNavigate();
  const { id: serviceId } = useParams();
  const isEdit = Boolean(serviceId);
  const [loading, setLoading] = useState(isEdit);
  const [formData, setFormData] = useState({
    serviceName: "",
    serviceCategory: "",
    contactName: "",
    contactCountryCode: DEFAULT_COUNTRY_CODE,
    contactPhone: "",
    altCountryCode: DEFAULT_COUNTRY_CODE,
    altPhone: "",
    supportEmail: "",
    supportHours: "",
    supportDays: "",
    notes: "",
    status: "Active",
    tollFreeNumber: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: formValueWithMobileSanitize(name, value) }));
    setError("");
  };

  const getUserId = () => {
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.user_id ?? user.id ?? 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }
    return 1;
  };

  useEffect(() => {
    if (!isEdit || !serviceId) return;
    const load = async () => {
      setLoading(true);
      try {
        const s = await fetchOfficeServiceById(serviceId);
        if (s) {
          setFormData({
            serviceName: s.Service_Name ?? s.service_name ?? "",
            serviceCategory: s.Service_Category ?? s.service_category ?? "",
            contactName: s.Contact_Name ?? s.contact_name ?? "",
            contactCountryCode: normalizeCountryCode(s.Contact_Country_Code),
            contactPhone: s.Contact_Phone ?? s.contact_phone ?? "",
            altCountryCode: normalizeCountryCode(s.Alt_Country_Code),
            altPhone: s.Alt_Phone ?? s.alt_phone ?? "",
            supportEmail: s.Support_Email ?? s.support_email ?? "",
            supportHours: s.Support_Hours ?? s.support_hours ?? "",
            supportDays: s.Support_Days ?? s.support_days ?? "",
            notes: s.Notes ?? s.notes ?? "",
            status: s.Status ?? s.status ?? "Active",
            tollFreeNumber: s.Toll_Free_Number ?? s.toll_free_number ?? ""
          });
        } else {
          alert("Service not found.");
        }
      } catch (err) {
        console.error("Error fetching office service:", err);
        alert("Failed to load service.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [serviceId, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.serviceName?.trim()) {
      setError("Service Name is required.");
      return;
    }
    if (!formData.serviceCategory?.trim()) {
      setError("Service Category is required.");
      return;
    }
    const contactError = mobileValidationMessage(formData.contactPhone, { label: "Contact phone" });
    if (contactError) {
      setError(contactError);
      return;
    }
    const altError = mobileValidationMessage(formData.altPhone, { label: "Alternate phone" });
    if (altError) {
      setError(altError);
      return;
    }

    const contactPhone = formData.contactPhone.trim();
    const altPhone = formData.altPhone.trim();
    const payload = {
      Service_Name: formatBusinessName(formData.serviceName),
      Service_Category: formatBusinessName(formData.serviceCategory),
      Contact_Name: formData.contactName.trim() ? formatPascal(formData.contactName) : null,
      Contact_Country_Code: contactPhone ? formData.contactCountryCode : null,
      Contact_Phone: contactPhone || null,
      Alt_Country_Code: altPhone ? formData.altCountryCode : null,
      Alt_Phone: altPhone || null,
      Support_Email: formData.supportEmail.trim() || null,
      Support_Hours: formData.supportHours.trim() || null,
      Support_Days: formData.supportDays.trim() || null,
      Notes: formData.notes.trim() || null,
      Status: formData.status || "Active",
      Toll_Free_Number: formData.tollFreeNumber.trim() || null
    };

    setSubmitting(true);
    try {
      if (isEdit) {
        payload.Updated_By = getUserId();
        await updateOfficeService(serviceId, payload);
        alert("Office service updated successfully.");
      } else {
        payload.Created_By = getUserId();
        await createOfficeService(payload);
        alert("Office service created successfully.");
      }
      if (onAddService) onAddService();
      if (onBack) onBack();
      else navigate("/support-contacts/service");
    } catch (err) {
      console.error("Error creating office service:", err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to create office service.";
      setError(msg);
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="addsupportcontact-container">
      <div className="addsupportcontact-header">
        <div className="addsupportcontact-title-section">
          <button
            type="button"
            className="addsupportcontact-back-link"
            onClick={() => (onBack ? onBack() : navigate("/support-contacts/service"))}
          >
            <FaArrowLeft /> Back to Support Service
          </button>
        </div>
      </div>

      <div className="addsupportcontact-form-card">
        {loading ? (
          <div className="addsupportcontact-loading">Loading service...</div>
        ) : (
          <form className="addsupportcontact-form" onSubmit={handleSubmit}>
          {error && (
            <div className="addsupportcontact-error" role="alert">
              {error}
            </div>
          )}
          <div className="addofficeservice-form-grid">
            <div className="addsupportcontact-form-group">
              <label>
                Service Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="serviceName"
                value={formData.serviceName}
                onChange={handleInputChange}
      
                required
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>
                Service Category <span className="required">*</span>
              </label>
              <input
                type="text"
                name="serviceCategory"
                value={formData.serviceCategory}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Contact Name</label>
              <input
                type="text"
                name="contactName"
                value={formData.contactName}
                onChange={handleInputChange}
               
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Contact Phone</label>
              <div className="adduser-phone-row">
                <CountryCodeSelect
                  value={formData.contactCountryCode}
                  onChange={(code) => setFormData((prev) => ({ ...prev, contactCountryCode: code }))}
                  ariaLabel="Contact phone country code"
                />
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  inputMode="numeric"
                />
              </div>
            </div>
            <div className="addsupportcontact-form-group">
              <label>Alt Phone</label>
              <div className="adduser-phone-row">
                <CountryCodeSelect
                  value={formData.altCountryCode}
                  onChange={(code) => setFormData((prev) => ({ ...prev, altCountryCode: code }))}
                  ariaLabel="Alt phone country code"
                />
                <input
                  type="tel"
                  name="altPhone"
                  value={formData.altPhone}
                  onChange={handleInputChange}
                  inputMode="numeric"
                />
              </div>
            </div>
            <div className="addsupportcontact-form-group">
              <label>Toll Free</label>
              <input
                type="text"
                name="tollFreeNumber"
                value={formData.tollFreeNumber}
                onChange={handleInputChange}
              
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Support Email</label>
              <input
                type="email"
                name="supportEmail"
                value={formData.supportEmail}
                onChange={handleInputChange}
              
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Support Hours</label>
              <input
                type="text"
                name="supportHours"
                value={formData.supportHours}
                onChange={handleInputChange}
              
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Support Days</label>
              <input
                type="text"
                name="supportDays"
                value={formData.supportDays}
                onChange={handleInputChange}
            
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className="addsupportcontact-form-group addofficeservice-notes-span">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
      
              />
            </div>
          </div>
          <div className="addsupportcontact-form-actions">
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? (isEdit ? "Updating..." : "Creating...") : (isEdit ? "Update Office Service" : "Create Office Service")}
            </button>
            {!isEdit ? (
              <button
                type="button"
                className="btn-cancel"
                onClick={() => (onBack ? onBack() : navigate("/support-contacts/service"))}
                disabled={submitting}
              >
                Cancel
              </button>
            ) : null}
          </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default AddOfficeService;
